/**
 * Shortcut Parsing & Matching
 *
 * Parses shortcut strings like 'mod+shift+c' or 'g g' into structured
 * representations and matches them against keyboard events.
 */

import type { ParsedShortcut, ParsedSequence, Platform, Modifiers } from './types';
import { modifiersMatch } from './platform';

// ============================================================================
// SHORTCUT PARSING
// ============================================================================

/**
 * Parse a shortcut string into its components.
 * Handles single shortcuts like 'mod+c' and sequences like 'g g' or 'mod+k mod+c'.
 */
export function parseShortcut(shortcut: string, timeout = 1000): ParsedSequence | null {
	if (!shortcut || shortcut.trim() === '') {
		return null;
	}

	const parts = shortcut.trim().split(/\s+/);
	const parsedParts: ParsedShortcut[] = [];

	for (const part of parts) {
		const parsed = parseSingleShortcut(part);
		if (parsed) {
			parsedParts.push(parsed);
		}
	}

	if (parsedParts.length === 0) {
		return null;
	}

	return {
		parts: parsedParts,
		timeout,
	};
}

/**
 * Parse a single shortcut part (no spaces).
 * E.g., 'mod+shift+c' -> { modifiers: {...}, key: 'c' }
 *
 * Special case: if the shortcut is ONLY a modifier key (e.g., 'shift'),
 * it's treated as the key to listen for, not a modifier requirement.
 */
function parseSingleShortcut(shortcut: string): ParsedShortcut | null {
	const pieces = shortcut.toLowerCase().split('+');
	const modifiers = {
		ctrl: false,
		alt: false,
		shift: false,
		meta: false,
		mod: false,
	};

	let key = '';

	// If there's only one piece and it's a modifier name, treat it as the key
	if (pieces.length === 1) {
		const piece = pieces[0];
		if (
			piece === 'shift' ||
			piece === 'ctrl' ||
			piece === 'control' ||
			piece === 'alt' ||
			piece === 'option' ||
			piece === 'meta' ||
			piece === 'cmd' ||
			piece === 'command'
		) {
			return { modifiers, key: piece };
		}
	}

	for (const piece of pieces) {
		switch (piece) {
			case 'ctrl':
			case 'control':
				modifiers.ctrl = true;
				break;
			case 'alt':
			case 'option':
				modifiers.alt = true;
				break;
			case 'shift':
				modifiers.shift = true;
				break;
			case 'meta':
			case 'cmd':
			case 'command':
				modifiers.meta = true;
				break;
			case 'mod':
				modifiers.mod = true;
				break;
			default:
				// This is the key
				key = piece;
		}
	}

	return { modifiers, key };
}

// ============================================================================
// SHORTCUT MATCHING
// ============================================================================

/**
 * Check if a key event matches a parsed shortcut part.
 */
export function matchesShortcut(
	key: string,
	eventMods: Modifiers,
	shortcut: ParsedShortcut,
	platform: Platform
): boolean {
	// Check key
	if (key.toLowerCase() !== shortcut.key.toLowerCase()) {
		return false;
	}

	// Check modifiers, passing the key so we can handle modifier keys correctly
	// (e.g., pressing 'alt' sets eventMods.alt, but should still match shortcut 'alt')
	return modifiersMatch(eventMods, shortcut.modifiers, platform, key);
}

/**
 * Check if a key is a pure modifier key (no character).
 */
export function isModifierKey(key: string): boolean {
	const lower = key.toLowerCase();
	return (
		lower === 'shift' ||
		lower === 'control' ||
		lower === 'ctrl' ||
		lower === 'alt' ||
		lower === 'option' ||
		lower === 'meta' ||
		lower === 'cmd' ||
		lower === 'command'
	);
}

/**
 * Normalize a key name for comparison.
 */
export function normalizeKey(key: string): string {
	const lower = key.toLowerCase();

	// Map various key names to canonical forms
	const keyMap: Record<string, string> = {
		control: 'ctrl',
		command: 'meta',
		cmd: 'meta',
		option: 'alt',
		escape: 'escape',
		esc: 'escape',
		return: 'enter',
		' ': 'space',
	};

	return keyMap[lower] || lower;
}

// ============================================================================
// SEQUENCE TRACKING
// ============================================================================

export interface SequenceState {
	/** The sequence being matched */
	sequence: ParsedSequence | null;
	/** Current position in the sequence (0-indexed) */
	position: number;
	/** When the sequence started */
	startTime: number;
	/** String representation of pending sequence for debug */
	pendingDisplay: string | null;
}

export function createSequenceState(): SequenceState {
	return {
		sequence: null,
		position: 0,
		startTime: 0,
		pendingDisplay: null,
	};
}

export function resetSequence(state: SequenceState): void {
	state.sequence = null;
	state.position = 0;
	state.startTime = 0;
	state.pendingDisplay = null;
}

/**
 * Try to advance sequence matching. Returns true if a complete sequence was matched.
 */
export function advanceSequence(
	state: SequenceState,
	key: string,
	mods: Modifiers,
	sequence: ParsedSequence,
	platform: Platform,
	now: number
): 'matched' | 'partial' | 'no-match' {
	const part = sequence.parts[state.position];

	if (!matchesShortcut(key, mods, part, platform)) {
		return 'no-match';
	}

	// Check timeout if we're continuing a sequence
	if (state.position > 0 && now - state.startTime > sequence.timeout) {
		return 'no-match';
	}

	// Advance position
	state.position++;

	if (state.position >= sequence.parts.length) {
		// Complete match
		return 'matched';
	}

	// Partial match - update state
	if (state.position === 1) {
		state.startTime = now;
		state.sequence = sequence;
	}

	// Build display string for pending sequence
	state.pendingDisplay = sequence.parts
		.slice(0, state.position)
		.map((p) => {
			const parts: string[] = [];
			if (p.modifiers.mod) parts.push('mod');
			if (p.modifiers.ctrl) parts.push('ctrl');
			if (p.modifiers.alt) parts.push('alt');
			if (p.modifiers.shift) parts.push('shift');
			if (p.modifiers.meta) parts.push('meta');
			parts.push(p.key);
			return parts.join('+');
		})
		.join(' ');

	return 'partial';
}
